
import { Component, Vue } from "vue-property-decorator";
import { API_TFile } from "@/models/document_cards";
import { SMApp, SMDocuments, SMUser, SMCases } from "@/store";
import pdf from "vue-pdf";
import VClamp from "vue-clamp";

import { CANDIDATE_ROLE, EMPLOYEE_ROLE, EMPLOYER_ROLE } from "@/constants/roles";
import {
  IN_PROGRESS_OA_STATUS,
  READY_TO_SUBMIT_OA_STATUS,
  CANDIDATE_ASSESSMENT_SERVICE_TYPE,
  IN_PROGRESS_SERVICE_STATUS,
  REPORT_CORRECTION_SERVICE_STATUS,
} from "@/constants/services";

import {
  DEFAULT_FILE_TYPE,
  COS_FILE_TYPE,
  IELTS_FILE_TYPE,
  PTE_FILE_TYPE,
} from "@/constants/files";

import { ARCHIVED_DOCUMENT_STATUS, APPROVED_DOCUMENT_STATUS } from "@/constants/documents";

import { EMPLOYEE_EMPLOYMENT_STATUS_LEAVER } from "@/constants/compliance";

const ICONS = {
  arrowBack: "arrow-back",
  download: "download-outline",
  chevronBack: "chevron-back-outline",
  chevronForward: "chevron-forward-outline",
};

interface IUploadPopup {
  isOpen: boolean;
  file: API_TFile | null;
}

@Component({
  components: {
    pdf,
    VClamp,
    ActionButtons: () => import("@/components/ActionContainer/ActionButtons.vue"),
    ActionContainerCard: () => import("@/components/ActionContainer/ActionContainerCard.vue"),
    DocumentCardDescription: () =>
      import("@/components/DocumentViewCards/views/Card/components/DocumentCardDescription.vue"),
    DocumentCardChecks: () =>
      import("@/components/DocumentViewCards/views/Card/components/DocumentCardChecks.vue"),
    DocumentCardUpload: () =>
      import("@/components/DocumentViewCards/views/Card/components/DocumentCardUpload.vue"),
    DocumentCardSelectDate: () =>
      import("@/components/DocumentViewCards/views/Card/components/DocumentCardSelectDate.vue"),
    DocumentCardDetails: () =>
      import("@/components/DocumentViewCards/views/Card/components/DocumentCardDetails.vue"),
  },
})
export default class DocumentCard extends Vue {
  icons = ICONS;

  get isExpert() {
    return SMUser.isExpert;
  }

  init = false;

  mounted() {
    this.init = true;
  }

  get showActionButtons() {
    return !this.isExpert && this.init;
  }

  get documentCard() {
    return SMDocuments.documentCard;
  }

  get fileType() {
    return this.documentCard?.document_template.file_type || DEFAULT_FILE_TYPE;
  }

  get isCos() {
    return this.fileType === COS_FILE_TYPE;
  }

  get isIelts() {
    return this.fileType === IELTS_FILE_TYPE;
  }

  get isPte() {
    return this.fileType === PTE_FILE_TYPE;
  }

  get isMLFileType() {
    return this.isCos || this.isIelts || this.isPte;
  }

  get hasSelectDate() {
    return this.isCos && !this.isDocumentCardArchived;
  }

  get hasDetails() {
    return this.isMLFileType && !this.isDocumentCardArchived;
  }

  PDFDialog = {
    isOpen: false,
    link: "",
    name: "",
  };

  isLoading = false;

  pageCount = 1;
  currentPage = 1;

  details = null;

  state = {
    isCorrect: true,
    isUploaderOpen: false,
  };

  get isDocumentCardArchived() {
    return this.documentCard?.status === ARCHIVED_DOCUMENT_STATUS;
  }

  get isDocumentCardAdditional() {
    return this.documentCard?.document_template.is_additional;
  }

  get canExpertEditFile() {
    if (!SMUser.isExpert) return false;

    // return this.documentCard?.document_template.uploaded_by_role === EXPERT_ROLE;

    // DEV-9203. Now "expert" can edit any file.
    return true;
  }

  get canEmployerEditFile() {
    if (!SMUser.isEmployer) return false;

    if (this.documentCard?.document_template.uploaded_by_role === EMPLOYEE_ROLE) return true;

    return this.documentCard?.document_template.uploaded_by_role === EMPLOYER_ROLE;
  }

  get canEmployeeEditFile() {
    if (!SMUser.isEmployee) return false;

    // DEV-9661% Candidate now transfer to Employee
    return [EMPLOYEE_ROLE, CANDIDATE_ROLE].includes(
      this.documentCard?.document_template.uploaded_by_role
    );
  }

  get canCandidateEditFile() {
    if (!SMUser.isCandidate) return false;

    return this.documentCard?.document_template.uploaded_by_role === CANDIDATE_ROLE;
  }

  get isDocumentCardCase() {
    return this.documentCard?.product_type === "case";
  }

  get isDocumentCardCompliance() {
    return this.documentCard?.product_type === "compliance";
  }

  get isAssessment() {
    return SMCases.caseType === CANDIDATE_ASSESSMENT_SERVICE_TYPE;
  }

  get canAssesmentEditFile() {
    if (!this.isDocumentCardCase) return false;

    if (!this.isAssessment) return false;

    return [IN_PROGRESS_SERVICE_STATUS, REPORT_CORRECTION_SERVICE_STATUS].includes(
      SMCases.caseStatus
    );
  }

  get canServicesEditFile() {
    if (!this.isDocumentCardCase) return false;

    if (this.isAssessment) return false;

    return [IN_PROGRESS_OA_STATUS, READY_TO_SUBMIT_OA_STATUS].includes(
      SMCases.onlineApplicationStatus
    );
  }

  get canComplianceEditFile() {
    return this.isDocumentCardCompliance && !SMUser.isComplianceBlocked;
  }

  get canEditFile() {
    if (SMUser.isExpert) return this.canExpertEditFile;

    if (SMUser.isEmployer) {
      if (this.isDocumentCardCompliance && SMUser.isComplianceBlocked) return false;
    }

    if (SMUser.isEmployee) {
      if (this.isDocumentCardCompliance && SMUser.isComplianceBlocked) return false;
    }

    if (this.isDocumentCardAdditional) return true;

    return (
      (this.canEmployeeEditFile || this.canEmployerEditFile || this.canCandidateEditFile) &&
      (this.canAssesmentEditFile || this.canServicesEditFile || this.canComplianceEditFile)
    );
  }

  get isApprovedByExpert() {
    return this.documentCard?.expert_status === APPROVED_DOCUMENT_STATUS;
  }

  get canUploadFile() {
    return !!(this.canEditFile && !this.isUploadDisabled);
  }

  get isEmployeeLeaver() {
    return SMApp.employeeWorkingStatus === EMPLOYEE_EMPLOYMENT_STATUS_LEAVER;
  }

  get checkEmployeeLeaver() {
    if (SMUser.isEmployee || SMUser.isEmployer) {
      if (this.isEmployeeLeaver) return false;
    }

    return true;
  }

  get canDeleteFile() {
    if (!this.checkEmployeeLeaver) return false;

    if (SMUser.isEmployee) {
      if (SMUser.isPremiumSubscriptionActive) {
        if (this.isApprovedByExpert) return false;

        return true;
      } else {
        return false;
      }
    }

    return this.canUploadFile;
  }

  get canArchiveFile() {
    if (!this.checkEmployeeLeaver) return false;

    if (SMUser.isEmployee && this.isApprovedByExpert) return false;

    return !(
      SMUser.isCandidate ||
      SMUser.isExpert ||
      (SMUser.isEmployer && this.$route.path.includes("cases"))
    );
  }

  get checks() {
    return SMDocuments.checks;
  }

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  unsetDocument() {
    SMDocuments.unsetCaseId();
    SMDocuments.unsetDocumentId();
    SMDocuments.unsetDocumentCard();
    SMDocuments.unsetChecks();
    SMDocuments.unsetSelectedChecks();
  }

  async goBack() {
    this.unsetDocument();

    this.$router.push(this.$route.path.substr(0, this.$route.path.lastIndexOf("/")));
  }

  async save() {
    await this.goBack();
  }

  openPDFDialog({ link, name }: { link: string; name: string }) {
    this.currentPage = 1;
    this.PDFDialog.link = link;
    this.PDFDialog.name = name;
    this.PDFDialog.isOpen = true;
  }

  update(payload: any) {
    this.details = payload;

    this.$emit("update");
  }

  downloadPDF() {
    const { link, name } = this.PDFDialog;

    SMDocuments.downloadFile({ link, name });
  }

  get isUploadDisabled() {
    if (SMUser.isEmployee && this.isApprovedByExpert) return true;

    return !(this.isUploadEnabled && this.canEditFile);
  }

  isUploadEnabled = true;

  updateUploadEnabled(payload: boolean) {
    this.isUploadEnabled = payload;
  }

  isFileUploaded = false;

  updateCardDetails() {
    this.isFileUploaded = true;
  }

  get isSaveDisabled() {
    if (!this.isMLFileType) return false;

    if (this.details && !this.isFileUploaded) return true;

    if (this.documentCard?.is_data_confirmed) return false;

    return !(this.canUploadFile && this.isFileUploaded);
  }
}
